import { EventEmitter } from "events";
import {
    getLocalStorageItem,
    setLocalStorageItem,
    LOCAL_STORAGE_KEYS,
} from "../utils/localStorage";

export interface TokenUsageData {
    budgetCap: number;
    periodUsage: number;
    requestUsage: number;
    resetTime: Date | null;
    lastUpdated: Date;
}

export interface RefillSettings {
    mode: "off" | "auto";
    millionsPerRefill: number;
}

class TokenUsageTracker extends EventEmitter {
    private static instance: TokenUsageTracker;
    private usageData: TokenUsageData | null = null;
    private refillSettings: RefillSettings = this.loadRefillSettings();

    // Price in cents per million tokens
    public static readonly PRICE_PER_MILLION_CENTS = 250;

    private constructor() {
        super();
        // Refill settings are already loaded in the property initialization
    }

    static getInstance(): TokenUsageTracker {
        if (!TokenUsageTracker.instance) {
            TokenUsageTracker.instance = new TokenUsageTracker();
        }
        return TokenUsageTracker.instance;
    }

    updateUsageFromHeaders(headers: Record<string, string>): void {
        const budgetCap = headers["x-budget-cap"] || headers["X-Budget-Cap"];
        const periodUsage =
            headers["x-period-usage"] || headers["X-Period-Usage"];
        const requestUsage =
            headers["x-request-usage"] || headers["X-Request-Usage"];
        const resetTime = headers["x-reset-time"] || headers["X-Reset-Time"];

        if (budgetCap && periodUsage && requestUsage) {
            this.usageData = {
                budgetCap: parseInt(budgetCap, 10),
                periodUsage: parseInt(periodUsage, 10),
                requestUsage: parseInt(requestUsage, 10),
                resetTime: resetTime ? new Date(resetTime) : null,
                lastUpdated: new Date(),
            };

            // Emit update event
            this.emit("usageUpdated", this.usageData);

            // Check if we need to trigger auto-refill
            this.checkAutoRefill();
        }
    }

    getUsageData(): TokenUsageData | null {
        return this.usageData;
    }

    getUsagePercentage(): number {
        if (!this.usageData) return 0;
        return (this.usageData.periodUsage / this.usageData.budgetCap) * 100;
    }

    getRemainingTokens(): number {
        if (!this.usageData) return 0;
        return this.usageData.budgetCap - this.usageData.periodUsage;
    }

    getRefillSettings(): RefillSettings {
        return { ...this.refillSettings };
    }

    updateRefillSettings(settings: Partial<RefillSettings>): void {
        this.refillSettings = { ...this.refillSettings, ...settings };
        this.saveRefillSettings();
        this.emit("refillSettingsUpdated", this.refillSettings);
    }

    getRefillCostCents(): number {
        return (
            this.refillSettings.millionsPerRefill *
            TokenUsageTracker.PRICE_PER_MILLION_CENTS
        );
    }

    getRefillCostFormatted(): string {
        const cents = this.getRefillCostCents();
        return `$${(cents / 100).toFixed(2)}`;
    }

    private loadRefillSettings(): RefillSettings {
        return getLocalStorageItem<RefillSettings>(
            LOCAL_STORAGE_KEYS.REFILL_SETTINGS,
            {
                mode: "off",
                millionsPerRefill: 2,
            }
        );
    }

    private saveRefillSettings(): void {
        setLocalStorageItem(
            LOCAL_STORAGE_KEYS.REFILL_SETTINGS,
            this.refillSettings
        );
    }

    private checkAutoRefill(): void {
        if (!this.usageData || this.refillSettings.mode !== "auto") return;

    }

    // Initialize by making a simple API call
    async initialize(apiCall: () => Promise<any>): Promise<void> {
        try {
            await apiCall();
            // The usage data will be updated automatically via updateUsageFromHeaders
        } catch (error) {
            console.error("Failed to initialize token usage:", error);
        }
    }
}

export const tokenUsageTracker = TokenUsageTracker.getInstance();
export default TokenUsageTracker;
